<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';

const store = useStore();
const canAddPurchases = computed(() => store.getters.hasPermission('purchase.add'));
const canEditPurchases = computed(() => store.getters.hasPermission('purchase.edit'));
const canDeletePurchases = computed(() => store.getters.hasPermission('purchase.delete'));
</script>

<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageaddheader
          :title="title"
          :title1="title1"
          :items="canAddPurchases ? items : []"
        />

        <div class="card">
          <div class="card-body">
            <div class="table-top">
              <div class="search-set">
                <div class="search-input">
                  <a class="btn btn-searchset">
                    <img src="../../../assets/img/icons/search-white.svg" alt="search" />
                  </a>
                  <div class="dataTables_filter">
                    <label>
                      <input
                        v-model="search"
                        type="search"
                        class="form-control form-control-sm"
                        placeholder="Search by bill no, supplier, invoice, item..."
                        @input="searching"
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div class="list-stats">
                <span class="stat-chip">
                  <span class="stat-dot dot-primary"></span>
                  Bills: <strong>{{ totalRecords }}</strong>
                </span>
                <span class="stat-chip">
                  <span class="stat-dot dot-success"></span>
                  Showing: <strong>{{ data.length }}</strong>
                </span>
              </div>
            </div>

            <div class="filter-row">
              <div class="filter-group">
                <label class="filter-label">From</label>
                <input v-model="filterFrom" type="date" class="form-control form-control-sm filter-input" @change="applyFilters" />
              </div>
              <div class="filter-group">
                <label class="filter-label">To</label>
                <input v-model="filterTo" type="date" class="form-control form-control-sm filter-input" @change="applyFilters" />
              </div>
              <div class="filter-group">
                <label class="filter-label">Payment Status</label>
                <select v-model="paymentStatus" class="form-control form-control-sm filter-input" @change="applyFilters">
                  <option value="">All</option>
                  <option value="PAID">Paid</option>
                  <option value="PARTIAL">Partial</option>
                  <option value="UNPAID">Unpaid</option>
                </select>
              </div>
              <button class="btn-clear-filter" @click="clearFilters">Clear</button>
            </div>

            <div v-if="loading" class="loading-wrap">
              <div class="loading-spinner"></div>
              <span>Loading purchases...</span>
            </div>

            <div v-else-if="!data.length" class="empty-wrap">
              <p class="empty-title">No purchases found</p>
              <p class="empty-sub" v-if="search">No results for "{{ search }}"</p>
              <p class="empty-sub" v-else>No purchase bills yet.</p>
            </div>

            <div v-else class="table-responsive">
              <a-table
                :columns="columns"
                :data-source="data"
                :loading="loading"
                :pagination="paginationConfig"
                :expanded-row-keys="expandedRowKeys"
                row-key="purchaseid"
                @change="handleTableChange"
                @expand="handleExpand"
              >
                <template #bodyCell="{ column, record }">
                  <template v-if="column.key === 'billno'">
                    <div class="bill-cell">
                      <button class="bill-badge bill-link" @click="viewPurchase(record.purchaseid)">{{ record.billno || 'No Bill' }}</button>
                      <small class="bill-id">{{ record.purchaseid }}</small>
                    </div>
                  </template>

                  <template v-else-if="column.key === 'vendor'">
                    <span class="supplier-tag">{{ record.vendor || '-' }}</span>
                  </template>

                  <template v-else-if="column.key === 'itemcount'">
                    <span class="qty-badge">{{ record.itemcount || 0 }} items</span>
                  </template>

                  <template v-else-if="column.key === 'totalqty'">
                    <span class="qty-badge qty-total">{{ formatQty(record.totalqty, record.totalfreeqty) }}</span>
                  </template>

                  <template v-else-if="column.key === 'netamount'">
                    <span class="price-total">Rs. {{ formatNumber(record.netamount) }}</span>
                  </template>

                  <template v-else-if="column.key === 'payment_status'">
                    <div class="payment-status-cell">
                      <span class="payment-status" :class="paymentStatusClass(record.payment_status)">
                        {{ paymentStatusLabel(record.payment_status) }}
                      </span>
                      <small v-if="record.is_credit" class="credit-note">
                        Paid Rs. {{ formatNumber(record.paid_amount || 0) }}
                      </small>
                    </div>
                  </template>

                  <template v-else-if="column.key === 'purchasedate' || column.key === 'invoicedate'">
                    <span class="date-text">{{ formatDate(record[column.key]) }}</span>
                  </template>

                  <template v-else-if="column.key === 'action'">
                    <div class="action-btns">
                      <button class="act-btn act-print" title="Print" @click="printPurchase(record)">
                        <img src="../../../assets/img/icons/printer.svg" alt="print" width="15" height="15" />
                      </button>
                      <button class="act-btn act-send" title="Send Bill" @click="sendPurchaseBill(record)">
                        <img src="../../../assets/img/icons/mail.svg" alt="send bill" width="15" height="15" />
                      </button>
                      <template v-if="canEditPurchases || canDeletePurchases">
                        <button v-if="canEditPurchases" class="act-btn act-edit" title="Edit" @click="editpurchase(record)">
                          <img src="../../../assets/img/icons/edit.svg" alt="edit" width="15" height="15" />
                        </button>
                        <button v-if="canDeletePurchases" class="act-btn act-delete" title="Delete" @click="showAlert(record.purchaseid)">
                          <img src="../../../assets/img/icons/delete.svg" alt="delete" width="15" height="15" />
                        </button>
                      </template>
                    </div>
                  </template>
                </template>

                <template #expandedRowRender="{ record }">
                  <div class="expanded-wrap">
                    <div v-if="detailLoading[record.purchaseid]" class="expanded-loading">Loading items...</div>
                    <div v-else-if="purchaseDetails[record.purchaseid] && purchaseDetails[record.purchaseid].items && purchaseDetails[record.purchaseid].items.length">
                      <div class="detail-header">
                        <span>Invoice No: <strong>{{ purchaseDetails[record.purchaseid].invoiceno || '-' }}</strong></span>
                        <span>Bill No: <strong>{{ purchaseDetails[record.purchaseid].billno || '-' }}</strong></span>
                        <span>Expense: <strong>{{ purchaseExpenseLabel(purchaseDetails[record.purchaseid]) }}</strong></span>
                        <span>Remarks: <strong>{{ purchaseDetails[record.purchaseid].remarks || '-' }}</strong></span>
                      </div>
                      <table class="detail-table">
                        <thead>
                          <tr>
                            <th>Item</th>
                            <th>Qty</th>
                            <th>Remaining</th>
                            <th>Free</th>
                            <th>Purchase Price</th>
                            <th>Net PTR</th>
                            <th>MRP</th>
                            <th>Wholesale</th>
                            <th>Tax Type</th>
                            <th>Tax %</th>
                            <th>Taxable</th>
                            <th>CGST</th>
                            <th>SGST</th>
                            <th>IGST</th>
                            <th>Tax Amt</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="item in detailItems(record.purchaseid)" :key="item.id">
                            <td>{{ item.itemname || item.itemid }}</td>
                            <td>{{ item.quantity }}</td>
                            <td>{{ item.remainingqty !== null && item.remainingqty !== undefined ? item.remainingqty : item.quantity }}</td>
                            <td>{{ item.freequantity || 0 }}</td>
                            <td>Rs. {{ formatNumber(item.purchaseprice) }}</td>
                            <td>Rs. {{ formatNumber(netPurchaseRate(item, purchaseDetails[record.purchaseid])) }}</td>
                            <td>Rs. {{ formatNumber(item.mrp) }}</td>
                            <td>Rs. {{ formatNumber(item.wholesaleprice) }}</td>
                            <td>{{ item.taxtype || 'GST' }}</td>
                            <td>{{ item.taxpercent || 0 }}</td>
                            <td>Rs. {{ formatNumber(item.taxablevalue) }}</td>
                            <td>Rs. {{ formatNumber(item.cgst) }}</td>
                            <td>Rs. {{ formatNumber(item.sgst) }}</td>
                            <td>Rs. {{ formatNumber(item.igst) }}</td>
                            <td>Rs. {{ formatNumber(item.taxamount) }}</td>
                          </tr>
                        </tbody>
                        <tfoot v-if="purchaseDetails[record.purchaseid]">
                          <tr class="detail-sum-row">
                            <td colspan="14" class="text-end"><strong>Total Amount</strong></td>
                            <td class="text-end"><strong>Rs. {{ formatNumber(purchaseDetails[record.purchaseid].totalamount) }}</strong></td>
                          </tr>
                          <tr class="detail-sum-row">
                            <td colspan="14" class="text-end"><strong>Taxable Value</strong></td>
                            <td class="text-end"><strong>Rs. {{ formatNumber(sumField(purchaseDetails[record.purchaseid].items, 'taxablevalue')) }}</strong></td>
                          </tr>
                          <tr class="detail-sum-row">
                            <td colspan="14" class="text-end"><strong>CGST</strong></td>
                            <td class="text-end"><strong>Rs. {{ formatNumber(sumField(purchaseDetails[record.purchaseid].items, 'cgst')) }}</strong></td>
                          </tr>
                          <tr class="detail-sum-row">
                            <td colspan="14" class="text-end"><strong>SGST</strong></td>
                            <td class="text-end"><strong>Rs. {{ formatNumber(sumField(purchaseDetails[record.purchaseid].items, 'sgst')) }}</strong></td>
                          </tr>
                          <tr class="detail-sum-row">
                            <td colspan="14" class="text-end"><strong>IGST</strong></td>
                            <td class="text-end"><strong>Rs. {{ formatNumber(sumField(purchaseDetails[record.purchaseid].items, 'igst')) }}</strong></td>
                          </tr>
                          <tr class="detail-sum-row">
                            <td colspan="14" class="text-end"><strong>Discount</strong></td>
                            <td class="text-end"><strong>- Rs. {{ formatNumber(totalDiscountDisplay(purchaseDetails[record.purchaseid])) }}</strong></td>
                          </tr>
                          <tr class="detail-sum-row">
                            <td colspan="14" class="text-end"><strong>Tax Amount</strong></td>
                            <td class="text-end"><strong>Rs. {{ formatNumber(purchaseDetails[record.purchaseid].taxamount) }}</strong></td>
                          </tr>
                          <tr class="detail-sum-row">
                            <td colspan="14" class="text-end"><strong>Round Off</strong></td>
                            <td class="text-end"><strong>Rs. {{ formatNumber(purchaseDetails[record.purchaseid].roundoff) }}</strong></td>
                          </tr>
                          <tr class="detail-sum-row detail-sum-row-net">
                            <td colspan="14" class="text-end"><strong>Net Amount</strong></td>
                            <td class="text-end"><strong>Rs. {{ formatNumber(purchaseDetails[record.purchaseid].netamount) }}</strong></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                    <div v-else class="expanded-loading">No items found.</div>
                  </div>
                </template>
              </a-table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import { buildBillPrintHtml, printBillHtml } from "../../../assets/utils/billPrint";
import { sendBillViaChannel } from "../../../services/billDelivery";
import { getCompanySettingsCached } from "@/services/companySettings";
import { normalizePhoneForWhatsApp } from "@/utils/phone";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.post["Content-Type"] = "application/json;charset=utf-8";
axios.defaults.headers.common["api-key"] = process.env.VUE_APP_API_KEY;

const columns = [
  { title: "Bill No", dataIndex: "billno", key: "billno" },
  { title: "Supplier", dataIndex: "vendor", key: "vendor" },
  { title: "Received Date", dataIndex: "purchasedate", key: "purchasedate" },
  { title: "Invoice Date", dataIndex: "invoicedate", key: "invoicedate" },
  { title: "Items", dataIndex: "itemcount", key: "itemcount" },
  { title: "Total Qty", dataIndex: "totalqty", key: "totalqty" },
  { title: "Net Amount", dataIndex: "netamount", key: "netamount" },
  { title: "Payment Status", dataIndex: "payment_status", key: "payment_status" },
  { title: "Action", key: "action", width: 130 },
];

export default {
  name: "purchaselist",
  data() {
    return {
      title: "Purchase List",
      title1: "Manage purchases by bill",
      items: [{ text: "Add New Purchase", addname: "/addpurchase" }],
      columns,
      data: [],
      loading: false,
      search: "",
      filterFrom: "",
      filterTo: "",
      paymentStatus: "",
      searchTimer: null,
      expandedRowKeys: [],
      purchaseDetails: {},
      detailLoading: {},
      itemCatalog: {},
      companySettings: null,
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
      },
    };
  },
  computed: {
    totalRecords() {
      return this.pagination.total || 0;
    },
    paginationConfig() {
      return {
        current: this.pagination.current,
        pageSize: this.pagination.pageSize,
        total: this.pagination.total,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} bills`,
      };
    },
  },
  methods: {
    escapeHtml(value) {
      return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    },
    handleKeydown(event) {
      if (
        this.$store.getters.hasPermission("purchase.add") &&
        this.$route.path === "/purchaselist" &&
        event.ctrlKey &&
        (event.key === "m" || event.key === "M")
      ) {
        event.preventDefault();
        this.$router.push("/addpurchase");
      }
    },
    applyFilters() {
      this.pagination.current = 1;
      this.getPurchases();
    },
    clearFilters() {
      this.filterFrom = "";
      this.filterTo = "";
      this.paymentStatus = "";
      this.pagination.current = 1;
      this.getPurchases();
    },
    searching() {
      this.pagination.current = 1;
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.getPurchases(), 300);
    },
    formatDate(val) {
      if (!val) return "-";
      const d = new Date(val);
      if (isNaN(d)) return val;
      return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    },
    formatNumber(val) {
      if (val === null || val === undefined || val === "") return "-";
      return Number(val).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatQty(qty, freeqty) {
      const base = Number(qty || 0);
      const free = Number(freeqty || 0);
      return free ? `${base} + ${free}` : `${base}`;
    },
    paymentStatusLabel(status) {
      const normalized = (status || "PAID").toUpperCase();
      if (normalized === "PARTIAL") return "Partial";
      if (normalized === "UNPAID") return "Unpaid";
      return "Paid";
    },
    paymentStatusClass(status) {
      const normalized = (status || "PAID").toUpperCase();
      return {
        "payment-paid": normalized === "PAID",
        "payment-partial": normalized === "PARTIAL",
        "payment-unpaid": normalized === "UNPAID",
      };
    },
    sumField(items, field) {
      return (items || []).reduce((sum, item) => sum + Number(item?.[field] || 0), 0);
    },
    normalizeDetailItem(item) {
      const qty = Number(item.quantity || 0);
      const price = Number(item.purchaseprice || 0);
      const discount = Math.min(100, Math.max(0, Number(item.discount || 0)));
      const taxPct = Math.min(100, Math.max(0, Number(item.taxpercent || 0)));
      const taxtype = String(item.taxtype || "GST").toUpperCase();
      const gross = qty * price;
      const discounted = gross * (1 - discount / 100);
      const savedTaxable = Number(item.taxablevalue || 0);
      const savedTax = Number(item.taxamount || 0);
      const savedCgst = Number(item.cgst || 0);
      const savedSgst = Number(item.sgst || 0);
      const savedIgst = Number(item.igst || 0);
      const hasSavedTaxBreakup = savedTaxable > 0 || savedTax > 0 || savedCgst > 0 || savedSgst > 0 || savedIgst > 0;

      if (taxPct <= 0 || taxtype === "EXEMPT" || taxtype === "NONGST" || hasSavedTaxBreakup) {
        return {
          ...item,
          discount,
          taxablevalue: savedTaxable,
          taxamount: savedTax,
          cgst: savedCgst,
          sgst: savedSgst,
          igst: savedIgst,
        };
      }

      let taxablevalue = 0;
      let taxamount = 0;
      if (item.istaxinclusive) {
        taxablevalue = discounted / (1 + taxPct / 100);
        taxamount = discounted - taxablevalue;
      } else {
        taxablevalue = discounted;
        taxamount = taxablevalue * taxPct / 100;
      }

      taxablevalue = Number(taxablevalue.toFixed(2));
      taxamount = Number(taxamount.toFixed(2));

      return {
        ...item,
        discount,
        taxablevalue,
        taxamount,
        cgst: Number((taxamount / 2).toFixed(2)),
        sgst: Number((taxamount / 2).toFixed(2)),
        igst: 0,
      };
    },
    rowTotal(item) {
      const qty = Number(item.quantity || 0);
      const price = Number(item.purchaseprice || 0);
      const discount = Number(item.discount || 0);
      const lineGross = qty * price * (1 - discount / 100);
      return item.istaxinclusive ? lineGross : (lineGross + Number(item.taxamount || 0));
    },
    lineDiscountAmount(item) {
      const qty = Number(item.quantity || 0);
      const price = Number(item.purchaseprice || 0);
      const discount = Number(item.discount || 0);
      return (qty * price * discount) / 100;
    },
    linePurchaseBase(item) {
      return Number(item.quantity || 0) * Number(item.purchaseprice || 0);
    },
    lineStockQty(item) {
      return Number(item.quantity || 0) + Number(item.freequantity || 0);
    },
    netPurchaseRate(item, detail) {
      if (item.netpurchaserate !== null && item.netpurchaserate !== undefined) {
        return Number(item.netpurchaserate || 0);
      }
      const ptr = Number(item.purchaseprice || 0);
      const stockQty = this.lineStockQty(item);
      const items = detail?.items || [];
      const totalBase = items.reduce((sum, row) => sum + this.linePurchaseBase(row), 0);
      const otherCost = this.purchaseExpenseAmount(detail);
      const lineBase = this.linePurchaseBase(item);
      if (!stockQty || !totalBase || !otherCost) return ptr;
      const lineOtherCost = otherCost * (lineBase / totalBase);
      return Number(((lineBase + lineOtherCost) / stockQty).toFixed(2));
    },
    purchaseExpense(detail) {
      return detail?.purchase_expense || detail?.purchase_expenses?.[0] || null;
    },
    purchaseExpenseAmount(detail) {
      return Number(this.purchaseExpense(detail)?.amount ?? detail?.miscellaneous_amount ?? 0);
    },
    formatExpenseType(type) {
      return String(type || "")
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },
    purchaseExpenseLabel(detail) {
      const amount = this.purchaseExpenseAmount(detail);
      if (!amount) return "-";
      const type = this.purchaseExpense(detail)?.expense_type || "MISCELLANEOUS";
      return `${this.formatExpenseType(type)}: Rs. ${this.formatNumber(amount)}`;
    },
    totalLineDiscount(items) {
      return (items || []).reduce((sum, item) => sum + this.lineDiscountAmount(item), 0);
    },
    totalDiscountDisplay(detail) {
      const lineDiscount = this.totalLineDiscount(detail?.items || []);
      const headerDiscount = Number(detail?.discountamount || 0);
      return lineDiscount + headerDiscount;
    },
    async hydrateItemNames(detail) {
      const missingIds = (detail.items || [])
        .filter(item => !this.itemCatalog[item.itemid])
        .map(item => item.itemid);

      await Promise.all(
        [...new Set(missingIds)].map(async (itemid) => {
          try {
            const res = await axios.get(`/v1/items/${itemid}`);
            if (res.data?.itemid) this.itemCatalog[itemid] = res.data.itemname;
          } catch (error) {
            console.error(error);
          }
        })
      );

      detail.items = (detail.items || []).map(item => ({
        ...this.normalizeDetailItem(item),
        itemname: this.itemCatalog[item.itemid] || item.itemname || item.itemid,
      }));
    },
    async fetchPurchaseDetail(purchaseid) {
      if (this.purchaseDetails[purchaseid] || this.detailLoading[purchaseid]) return;
      this.detailLoading = { ...this.detailLoading, [purchaseid]: true };
      try {
        const res = await axios.get(`/v1/purchases/${purchaseid}`);
        await this.hydrateItemNames(res.data);
        this.purchaseDetails = { ...this.purchaseDetails, [purchaseid]: res.data };
      } catch (error) {
        console.error(error);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load purchase items." });
      } finally {
        this.detailLoading = { ...this.detailLoading, [purchaseid]: false };
      }
    },
    async fetchCompanySettings() {
      if (this.companySettings) return;
      try {
        this.companySettings = await getCompanySettingsCached();
      } catch (error) {
        console.error(error);
        this.companySettings = {};
      }
    },
    async buildPurchaseBillDocument(record) {
      const purchaseid = record?.purchaseid;
      if (!purchaseid) return null;

      await this.fetchPurchaseDetail(purchaseid);
      await this.fetchCompanySettings();
      const detail = this.purchaseDetails[purchaseid];
      if (!detail) {
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load purchase bill." });
        return null;
      }

      const safeDate = (val) => {
        const out = this.formatDate(val);
        return out && out !== "-" ? out : "-";
      };

      const billNo = detail.billno || "";
      const items = detail.items || [];
      const documentTitle = `Purchase Bill ${billNo}`;
      const html = buildBillPrintHtml({
        documentTitle,
        title: "Purchase Bill",
        company: this.companySettings,
        partyLabel: "Supplier",
        partyRows: [
          { label: "Supplier",    value: record.vendor || detail.vendor || "-" },
          { label: "Invoice No",  value: detail.invoiceno },
        ],
        billRows: [
          { label: "Bill No",      value: billNo },
          { label: "Invoice Date", value: safeDate(detail.invoicedate) },
          { label: "Received",     value: this.formatDate(record.purchasedate) || record.purchasedate || "-" },
        ],
        columns: [
          { label: "#",       style: "width:26px;" },
          { label: "Item",    className: "item-col" },
          { label: "Qty",     className: "r", style: "width:44px;" },
          { label: "Price",   className: "r", style: "width:68px;" },
          { label: "Net PTR", className: "r", style: "width:68px;" },
          { label: "MRP",     className: "r", style: "width:60px;" },
          { label: "Disc%",   className: "r", style: "width:44px;" },
          { label: "Taxable", className: "r", style: "width:68px;" },
          { label: "GST%",    className: "c", style: "width:36px;" },
          { label: "Tax",     className: "r", style: "width:64px;" },
          { label: "Total",   className: "r", style: "width:68px;" },
        ],
        rows: items.map((item, idx) => {
          const qty     = Number(item.quantity || 0);
          const price   = Number(item.purchaseprice || 0);
          const netPtr  = this.netPurchaseRate(item, detail);
          const mrp     = Number(item.mrp || 0);
          const discPct = Number(item.discount || 0);
          const taxable = Number(item.taxablevalue || 0);
          const taxPct  = Number(item.taxpercent || 0);
          const taxAmt  = Number(item.taxamount || 0);
          const total   = Number(this.rowTotal(item) || 0);
          const expiry  = safeDate(item.expirydate);
          const incl    = item.istaxinclusive ? " (Incl)" : "";
          return [
            { text: idx + 1, className: "c" },
            { html: `${String(item.itemname || item.itemid || "")}<span class="sub">Batch: ${String(item.batchno || "-")}${expiry !== "-" ? "  Exp: " + expiry : ""}${incl}</span>`, className: "item-name-cell" },
            { text: this.formatNumber(qty), className: "r" },
            { text: `Rs. ${this.formatNumber(price)}`, className: "r" },
            { text: `Rs. ${this.formatNumber(netPtr)}`, className: "r" },
            { text: mrp ? `Rs. ${this.formatNumber(mrp)}` : "-", className: "r" },
            { text: discPct ? String(discPct) : "-", className: "r" },
            { text: `Rs. ${this.formatNumber(taxable)}`, className: "r" },
            { text: String(taxPct), className: "c" },
            { text: `Rs. ${this.formatNumber(taxAmt)}`, className: "r" },
            { text: `Rs. ${this.formatNumber(total)}`, className: "r" },
          ];
        }),
        subTotalRow: [
          { text: "", className: "c" },
          { text: "Sub-Total" },
          { text: this.formatNumber(items.reduce((s, i) => s + Number(i.quantity || 0), 0)), className: "r" },
          { text: "" }, { text: "" }, { text: "" }, { text: "" },
          { text: `Rs. ${this.formatNumber(items.reduce((s, i) => s + Number(i.taxablevalue || 0), 0))}`, className: "r" },
          { text: "" },
          { text: `Rs. ${this.formatNumber(items.reduce((s, i) => s + Number(i.taxamount || 0), 0))}`, className: "r" },
          { text: `Rs. ${this.formatNumber(items.reduce((s, i) => s + this.rowTotal(i), 0))}`, className: "r" },
        ],
        totals: [
          { label: "Taxable Value", value: `Rs. ${this.formatNumber(items.reduce((s, i) => s + Number(i.taxablevalue || 0), 0))}` },
          { label: "CGST",         value: `Rs. ${this.formatNumber(items.reduce((s, i) => s + Number(i.cgst || 0), 0))}` },
          { label: "SGST",         value: `Rs. ${this.formatNumber(items.reduce((s, i) => s + Number(i.sgst || 0), 0))}` },
          { label: "Discount",     value: `- Rs. ${this.formatNumber(this.totalDiscountDisplay(detail))}` },
          { label: "Tax",          value: `Rs. ${this.formatNumber(detail.taxamount || 0)}` },
          ...(this.purchaseExpenseAmount(detail) ? [
            { label: this.formatExpenseType(this.purchaseExpense(detail)?.expense_type || "MISCELLANEOUS"), value: `Rs. ${this.formatNumber(this.purchaseExpenseAmount(detail))}` },
          ] : []),
          { label: "Round Off",    value: `Rs. ${this.formatNumber(detail.roundoff || 0)}` },
          { label: "Net Amount",   value: `Rs. ${this.formatNumber(detail.netamount || 0)}` },
        ],
        footerNote: this.companySettings?.InvoiceFooter || this.companySettings?.InvoiceTerms,
        itemColumnWidth: "24%",
        netAmount: detail.netamount,
      });

      return {
        html,
        billNo,
        documentTitle,
        partyName: record.vendor || detail.vendor || "Supplier",
        email: detail.email || record.email || "",
        phoneNumber: detail.phone || record.phone || "",
      };
    },
    async printPurchase(record) {
      const bill = await this.buildPurchaseBillDocument(record);
      if (!bill) return;

      printBillHtml(bill.html, (text) => {
        Swal.fire({ icon: "error", title: "Print Error", text });
      });
    },
    escapeHtml(value) {
      return String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
    async sendPurchaseBill(record) {
      const bill = await this.buildPurchaseBillDocument(record);
      if (!bill) return;

      const emailValue = this.escapeHtml(bill.email);
      const phoneValue = this.escapeHtml(bill.phoneNumber);
      const { value: delivery } = await Swal.fire({
        title: "Send bill",
        html: `
          <div class="text-start">
            <label style="display:block;margin-bottom:8px;">
              <input type="checkbox" id="send-email" ${emailValue ? "checked" : ""} />
              Email
            </label>
            <input id="recipient-email" type="email" class="swal2-input" placeholder="supplier@example.com" value="${emailValue}" style="margin:0 0 14px;width:100%;" />
            <label style="display:block;margin-bottom:8px;">
              <input type="checkbox" id="send-whatsapp" ${phoneValue ? "checked" : ""} />
              WhatsApp
            </label>
            <input id="recipient-phone" type="tel" class="swal2-input" placeholder="+919876543210" value="${phoneValue}" style="margin:0;width:100%;" />
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: "Send",
        preConfirm: () => {
          const sendEmail = document.getElementById("send-email").checked;
          const sendWhatsapp = document.getElementById("send-whatsapp").checked;
          const email = document.getElementById("recipient-email").value.trim();
          const phoneNumber = document.getElementById("recipient-phone").value.trim();
          const normalizedPhone = normalizePhoneForWhatsApp(phoneNumber);

          if (!sendEmail && !sendWhatsapp) {
            Swal.showValidationMessage("Select email, WhatsApp, or both.");
            return false;
          }
          if (sendEmail && !email) {
            Swal.showValidationMessage("Email is required.");
            return false;
          }
          if (sendWhatsapp && !phoneNumber) {
            Swal.showValidationMessage("Phone number is required.");
            return false;
          }
          if (sendWhatsapp && !normalizedPhone.valid) {
            Swal.showValidationMessage("Enter a valid WhatsApp phone number.");
            return false;
          }
          return { sendEmail, sendWhatsapp, email, phoneNumber: normalizedPhone.digits };
        },
      });
      if (!delivery) return;

      try {
        Swal.fire({ title: "Sending bill...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
        const documentPayload = {
          document_title: bill.documentTitle,
          bill_no: bill.billNo,
          html: bill.html,
        };
        const sendTasks = [];
        if (delivery.sendEmail) {
          sendTasks.push(sendBillViaChannel("email", {
            recipient: delivery.email,
            subject: `${bill.documentTitle}`,
            message: `Please find attached ${bill.documentTitle}.`,
            reference_type: "PURCHASE",
            reference_id: record.purchaseid,
            message_type: "BILL",
            ...documentPayload,
          }));
        }
        if (delivery.sendWhatsapp) {
          sendTasks.push(sendBillViaChannel("whatsapp", {
            phone_number: delivery.phoneNumber,
            customer_name: bill.partyName,
            order_number: bill.billNo,
            caption: `Please find attached ${bill.documentTitle}.`,
            reference_type: "PURCHASE",
            reference_id: record.purchaseid,
            message_type: "BILL",
            ...documentPayload,
          }));
        }
        await Promise.all(sendTasks);

        const sentTo = [
          delivery.sendEmail ? `email ${delivery.email}` : "",
          delivery.sendWhatsapp ? `WhatsApp ${delivery.phoneNumber}` : "",
        ].filter(Boolean).join(" and ");
        Swal.fire({ icon: "success", title: "Sent", text: `Bill sent by ${sentTo}.`, timer: 1800, showConfirmButton: false });
      } catch (error) {
        console.error(error);
        Swal.fire({ icon: "error", title: "Send Error", text: error.response?.data?.detail || "Could not send the bill." });
      }
    },
    handleExpand(expanded, record) {
      if (expanded) {
        this.expandedRowKeys = [...new Set([...this.expandedRowKeys, record.purchaseid])];
        this.fetchPurchaseDetail(record.purchaseid);
      } else {
        this.expandedRowKeys = this.expandedRowKeys.filter(id => id !== record.purchaseid);
      }
    },
    detailItems(purchaseid) {
      return this.purchaseDetails[purchaseid]?.items || [];
    },
    async getPurchases() {
      this.loading = true;
      try {
        const res = await axios.get("/v1/purchases", {
          params: {
            page: this.pagination.current,
            page_size: this.pagination.pageSize,
            search: this.search.trim() || undefined,
            date_from: this.filterFrom || undefined,
            date_to: this.filterTo || undefined,
            payment_status: this.paymentStatus || undefined,
          },
        });
        this.data = res.data.items || [];
        this.pagination = {
          current: res.data.page || this.pagination.current,
          pageSize: res.data.page_size || this.pagination.pageSize,
          total: res.data.total || 0,
        };
      } catch (error) {
        console.error(error);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load purchases." });
      } finally {
        this.loading = false;
      }
    },
    handleTableChange(pagination) {
      this.pagination.current = pagination.current;
      this.pagination.pageSize = pagination.pageSize;
      this.getPurchases();
    },
    editpurchase(record) {
      this.$router.push({ path: "/editpurchase", query: { id: record.purchaseid } });
    },
    viewPurchase(purchaseid) {
      this.$router.push({ path: "/purchase-details", query: { id: purchaseid } });
    },
    showAlert(id) {
      Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#FF9F43",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      }).then((result) => {
        if (!result.isConfirmed) return;
        axios.delete("/v1/purchases/" + id)
          .then(() => {
            this.data = this.data.filter(row => row.purchaseid !== id);
            this.pagination.total = Math.max(0, this.pagination.total - 1);
            this.expandedRowKeys = this.expandedRowKeys.filter(rowId => rowId !== id);
            Swal.fire({ icon: "success", title: "Deleted!", text: "Purchase bill has been deleted.", timer: 1500, showConfirmButton: false });
          })
          .catch((error) => {
            Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Could not delete." });
          });
      });
    },
  },
  mounted() {
    document.addEventListener("keydown", this.handleKeydown);
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
    document.removeEventListener("keydown", this.handleKeydown);
  },
  beforeMount() {
    this.getPurchases();
  },
};
</script>

<style scoped>
.table-top { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:16px; }
.search-input { display:flex; align-items:center; gap:6px; }
.search-input .form-control { width:320px; height:36px; border-radius:6px; border:1px solid #e5e7eb; padding:0 12px; }
.list-stats { display:flex; gap:10px; align-items:center; }
.stat-chip { display:flex; align-items:center; gap:5px; font-size:0.82rem; color:#6b7280; background:#f9fafb; border:1px solid #e5e7eb; border-radius:20px; padding:3px 10px; }
.stat-dot { width:7px; height:7px; border-radius:50%; }
.dot-primary { background:#fb923c; }
.dot-success { background:#22c55e; }
.filter-row { display:flex; gap:10px; align-items:end; flex-wrap:wrap; margin-bottom:16px; }
.filter-group { display:flex; flex-direction:column; gap:4px; }
.filter-label { font-size:0.75rem; color:#6b7280; font-weight:600; }
.filter-input { width:150px; height:36px; border-radius:6px; border:1px solid #e5e7eb; }
.btn-clear-filter { height:36px; padding:0 14px; border:none; border-radius:6px; background:#f3f4f6; color:#374151; font-size:0.85rem; font-weight:600; }
.loading-wrap, .empty-wrap { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:50px 20px; gap:10px; }
.loading-spinner { width:36px; height:36px; border:3px solid #f3f4f6; border-top-color:#fb923c; border-radius:50%; animation:spin .7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.bill-cell { display:flex; flex-direction:column; gap:4px; }
.bill-badge { display:inline-block; background:#fff7f0; color:#ea580c; border:1px solid #fdba74; border-radius:20px; padding:2px 10px; font-size:.78rem; font-weight:700; width:max-content; }
.bill-link { cursor:pointer; line-height:1.4; }
.bill-link:hover { background:#ffedd5; color:#c2410c; }
.bill-id { color:#9ca3af; font-size:.72rem; }
.supplier-tag { display:inline-block; padding:2px 10px; background:#f0fdf4; color:#16a34a; border:1px solid #bbf7d0; border-radius:20px; font-size:.78rem; font-weight:500; }
.qty-badge { display:inline-block; background:#eff6ff; color:#2563eb; border:1px solid #bfdbfe; border-radius:20px; padding:2px 10px; font-size:.78rem; font-weight:600; }
.qty-total { background:#ecfeff; color:#0f766e; border-color:#99f6e4; }
.date-text { font-size:.82rem; color:#6b7280; }
.price-total { color:#ea580c; font-weight:700; }
.payment-status-cell { display:flex; flex-direction:column; gap:4px; align-items:flex-start; }
.payment-status { display:inline-block; border-radius:20px; padding:2px 10px; font-size:.76rem; font-weight:700; text-transform:uppercase; }
.payment-paid { background:#dcfce7; color:#166534; border:1px solid #86efac; }
.payment-partial { background:#fef3c7; color:#92400e; border:1px solid #fcd34d; }
.payment-unpaid { background:#fee2e2; color:#b91c1c; border:1px solid #fca5a5; }
.credit-note { color:#6b7280; font-size:.72rem; }
.action-btns { display:flex; gap:6px; }
.act-btn { width:30px; height:30px; display:inline-flex; align-items:center; justify-content:center; border:none; border-radius:6px; }
.act-print { background:#f3f4f6; }
.act-send { background:#ecfdf5; }
.act-edit { background:#eff6ff; }
.act-delete { background:#fef2f2; }
.expanded-wrap { background:#fffaf5; border:1px solid #fed7aa; border-radius:10px; padding:14px; }
.detail-header { display:flex; gap:18px; flex-wrap:wrap; margin-bottom:10px; font-size:.82rem; color:#6b7280; }
.detail-table { width:100%; border-collapse:collapse; }
.detail-table th, .detail-table td { padding:8px 10px; border-bottom:1px solid #f3e8d8; font-size:.82rem; text-align:left; }
.detail-table th { color:#6b7280; font-weight:700; text-transform:uppercase; font-size:.72rem; }
.detail-table tfoot td { border-bottom:none; border-top:1px solid #f3e8d8; }
.detail-sum-row td { background:#fff7f0; }
.detail-sum-row-net td { background:#ffedd5; }
.expanded-loading { color:#6b7280; font-size:.82rem; }
</style>
